import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { Lightbulb, Pencil, Send, Trash2 } from "lucide-react";
import "react-toastify/dist/ReactToastify.css";
import "./styles/featureSuggestion.css";
import "./styles/modern-pages.css";

const FeatureSuggestion = () => {
  const baseUrl = process.env.REACT_APP_DJANGO_API_BASE_URL;
  const username = localStorage.getItem("username");
  const token = localStorage.getItem("token");

  const [formData, setFormData] = useState({
    title: "",
    description: "",
  });
  const [suggestions, setSuggestions] = useState([]);
  const [editingId, setEditingId] = useState(null);

  const formRef = useRef(null); // Reference to the form to scroll to it

  useEffect(() => {
    fetchSuggestions();
  }, []);

  const fetchSuggestions = async () => {
    try {
      const response = await axios.get(`${baseUrl}/api/feature-suggestions/`);
      setSuggestions(response.data);
    } catch (error) {
      console.error("Failed to fetch suggestions", error);
      toast.error("Failed to load suggestions.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      username,
    };

    try {
      if (editingId) {
        // If editing an existing suggestion
        await axios.put(`${baseUrl}/api/feature-suggestions/${editingId}/`, payload, {
          headers: {
            Authorization: `Token ${token}`,
          },
        });
        toast.success("Suggestion updated!");
        setEditingId(null); // Reset editing state
      } else {
        // If creating a new suggestion
        await axios.post(`${baseUrl}/api/feature-suggestions/`, payload, {
          headers: {
            Authorization: `Token ${token}`,
          },
        });
        toast.success("Feature suggestion submitted successfully!");
      }
      setFormData({ title: "", description: "" });
      fetchSuggestions(); // Refresh the suggestions list
    } catch (error) {
      console.error("Submit failed", error);
      toast.error("Something went wrong. Please try again.");
    }
  };

  const handleEdit = (item) => {
    setFormData({ title: item.title, description: item.description });
    setEditingId(item.id); // Set the item to be edited
    
    // Scroll to the form container (smoothly)
    formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this suggestion?")) return;

    try {
      await axios.delete(`${baseUrl}/api/feature-suggestions/${id}/`, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });
      toast.success("Suggestion deleted successfully!");
      fetchSuggestions();
    } catch (error) {
      console.error("Delete failed", error);
      toast.error("Failed to delete suggestion.");
    }
  };

  return (
    <div className="modern-page feature-page">
      <div className="modern-page-shell">
        <header className="modern-page-header">
          <h1 className="modern-page-title">Suggest a Feature</h1>
          <span className="modern-page-meta"><Lightbulb size={14} /> {suggestions.length} suggestions</span>
        </header>

        <div className="feature-layout">
          <section className="modern-panel feature-form-panel" ref={formRef}>
            <div className="modern-panel-header"><h2>{editingId ? "Edit Suggestion" : "New Suggestion"}</h2></div>
            <form onSubmit={handleSubmit} className="feature-form">
              <label className="feature-field">
                <span>Title</span>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  className="form-input"
                />
              </label>
              <label className="feature-field">
                <span>Description</span>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  rows="6"
                  className="form-textarea"
                />
              </label>
              <button type="submit" className="modern-primary-button submit-button">
                <Send size={15} /> {editingId ? "Update" : "Submit"}
              </button>
            </form>
          </section>

          <section className="modern-panel suggestions-panel">
            <div className="modern-panel-header"><h2>Submitted Suggestions</h2></div>
            {suggestions.length === 0 ? (
              <div className="modern-empty-state"><Lightbulb size={24} /><span>No suggestions yet.</span></div>
            ) : (
              <ul className="suggestion-list">
                {suggestions.map((item) => {
                  const statusClass = `status-${item.status.toLowerCase().replace(/\s+/g, "-")}`;
                  return (
                    <li key={item.id} className={`suggestion-item ${statusClass}`}>
                      <div className="suggestion-header">
                        <span className="suggestion-title">{item.title}</span>
                        <span className={`suggestion-status ${statusClass}`}>{item.status}</span>
                      </div>
                      <p className="suggestion-description">{item.description}</p>
                      <small className="suggestion-meta">
                        {item.username} · {item.reference_code || `#${item.id}`}
                      </small>
                      {item.status === "Pending Review" && item.username === username && (
                        <div className="suggestion-actions">
                          <button type="button" className="edit-btn" onClick={() => handleEdit(item)}><Pencil size={14} /> Edit</button>
                          <button type="button" className="delete-btn" onClick={() => handleDelete(item.id)}><Trash2 size={14} /> Delete</button>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>
        <ToastContainer />
      </div>
    </div>
  );
};

export default FeatureSuggestion;
